import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import { SplitFlapRow } from "../../features/facility/components/queue/splitflap/QueueDisplay";
import {
  resumeClickAudio,
  playSwoosh,
  playClickVariant,
} from "./splitflapAudio";
import "./TitleLogosPage.css";
import { tokens } from "@healthdesk/shared-utils";

/** Default 3D display parameters (match QueueDisplay DISPLAY_3D_DEFAULTS). */
const DEFAULT_DISPLAY_3D = {
  perspectivePx: 530,
  perspectiveOriginYPercent: 42,
  flapTranslateZPx: 1,
  seamTranslateZPx: 2,
  capTranslateZPx: 3,
  showThicknessEdge: true,
  phaseLighting: true,
  seamHeightPx: 3,
  hingeHoldMs: 10,
  charJitterMs: 2,
  edgeDarkening: true,
  edgeDarkeningStrength: 0.12,
  bottomOvershootDeg: 0,
  capWidthPx: 7,
  capHeightPx: 12,
};

/** Fixed settings for the MSI³ display. */
const TITLE_LOGO_SETTINGS = {
  swooshOn: true,
  swooshFreqHz: 3850,
  soundOn: true,
  soundVariant: 6,
  modalTapFreq1Hz: 600,
  modalTapFreq2Hz: 1200,
  modalTapGain1Pct: 7,
  modalTapGain2Pct: 12,
  flipDurationMs: 20,
  charStaggerMs: 0,
  speedMs: 24,
  bold: true,
};

/** MSI³ brand only (single split-flap row for this page). */
const MSI3_BRAND = {
  id: "msi3",
  label: "MSI³",
  text: "MSI",
  lowerFlap: tokens.splitFlap.msiTitleLower,
  upperFlap: tokens.splitFlap.msiTitleUpper,
  superscript: "3",
};

const FRAME_COLOR = tokens.splash.github.gradientEnd;
const CHARACTER_COLOR = "common.white";
const BOARD_BG = tokens.colors.navGlobalManagerBoard.light;

const FRAME_TIME_THRESHOLD_MS = 28;
const RAF_SAMPLES = 8;

function use3dCapability() {
  const [use3d, setUse3d] = useState(true);

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setUse3d(false);
      return;
    }

    let rafId;
    const frameTimes = [];
    let last = performance.now();

    const measure = () => {
      const now = performance.now();
      frameTimes.push(now - last);
      last = now;
      if (frameTimes.length < RAF_SAMPLES) {
        rafId = requestAnimationFrame(measure);
      } else {
        const avg = frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length;
        if (avg > FRAME_TIME_THRESHOLD_MS) setUse3d(false);
      }
    };

    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return use3d;
}

function clampNum(val, min, max, fallback) {
  const n = Number(val);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

const MSI3OnlyPage = () => {
  const capability3d = use3dCapability();
  const [use3dCheckbox, setUse3dCheckbox] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);
  const [paramsModalOpen, setParamsModalOpen] = useState(false);
  const [display3D, setDisplay3D] = useState(() => ({ ...DEFAULT_DISPLAY_3D }));
  const [flipDurationMs, setFlipDurationMs] = useState(TITLE_LOGO_SETTINGS.flipDurationMs);

  const use3d = capability3d && use3dCheckbox;

  const handleDisplay3DChange = (key, value) => {
    setDisplay3D((prev) => ({ ...prev, [key]: value }));
  };
  const handleResetParams = () => {
    setDisplay3D({ ...DEFAULT_DISPLAY_3D });
    setFlipDurationMs(TITLE_LOGO_SETTINGS.flipDurationMs);
  };

  const onFlapComplete = useCallback(() => {
    resumeClickAudio();
    if (TITLE_LOGO_SETTINGS.soundOn) {
      playClickVariant(TITLE_LOGO_SETTINGS.soundVariant, {
        freqs: [TITLE_LOGO_SETTINGS.modalTapFreq1Hz, TITLE_LOGO_SETTINGS.modalTapFreq2Hz],
        gain1Pct: TITLE_LOGO_SETTINGS.modalTapGain1Pct,
        gain2Pct: TITLE_LOGO_SETTINGS.modalTapGain2Pct,
      });
    }
    if (TITLE_LOGO_SETTINGS.swooshOn) {
      playSwoosh({
        startFreqHz: TITLE_LOGO_SETTINGS.swooshFreqHz,
        gainPct: 100,
      });
    }
  }, []);

  const brand = MSI3_BRAND;

  return (
    <div className="title-logos-page">
      <div className="title-logos-header">
        <Link to="/splash/title-logos" className="title-logos-back">
          ← Title logos
        </Link>
        <h1 className="title-logos-title">MSI³ split-flap</h1>
        {!capability3d && use3dCheckbox && (
          <p className="title-logos-3d-off" aria-live="polite">
            3D animation is off for performance on this device.
          </p>
        )}
        <div className="title-logos-controls">
          <button
            type="button"
            className="title-logos-btn"
            onClick={() => setAnimationKey((k) => k + 1)}
          >
            RUN
          </button>
          <button
            type="button"
            className="title-logos-btn title-logos-btn-secondary"
            onClick={() => setParamsModalOpen(true)}
          >
            Display parameters
          </button>
          <label className="title-logos-checkbox-label">
            <input
              type="checkbox"
              checked={use3dCheckbox}
              onChange={(e) => setUse3dCheckbox(e.target.checked)}
              aria-label="3D animation on"
            />
            3D
          </label>
        </div>
      </div>

      <Dialog open={paramsModalOpen} onClose={() => setParamsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>3D display parameters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              type="number"
              label={`Perspective (px) (${DEFAULT_DISPLAY_3D.perspectivePx})`}
              value={display3D.perspectivePx}
              onChange={(e) => handleDisplay3DChange("perspectivePx", clampNum(e.target.value, 300, 1200, 530))}
              inputProps={{ min: 300, max: 1200, step: 20 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Perspective origin Y (%) (${DEFAULT_DISPLAY_3D.perspectiveOriginYPercent})`}
              value={display3D.perspectiveOriginYPercent}
              onChange={(e) => handleDisplay3DChange("perspectiveOriginYPercent", clampNum(e.target.value, 0, 100, 42))}
              inputProps={{ min: 0, max: 100, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Flip duration (ms) (${TITLE_LOGO_SETTINGS.flipDurationMs})`}
              value={flipDurationMs}
              onChange={(e) => setFlipDurationMs(clampNum(e.target.value, 10, 150, 20))}
              inputProps={{ min: 10, max: 150, step: 5 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Flap Z (px) (${DEFAULT_DISPLAY_3D.flapTranslateZPx})`}
              value={display3D.flapTranslateZPx}
              onChange={(e) => handleDisplay3DChange("flapTranslateZPx", clampNum(e.target.value, 0, 10, 1))}
              inputProps={{ min: 0, max: 10, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Seam Z (px) (${DEFAULT_DISPLAY_3D.seamTranslateZPx})`}
              value={display3D.seamTranslateZPx}
              onChange={(e) => handleDisplay3DChange("seamTranslateZPx", clampNum(e.target.value, 0, 10, 2))}
              inputProps={{ min: 0, max: 10, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Cap Z (px) (${DEFAULT_DISPLAY_3D.capTranslateZPx})`}
              value={display3D.capTranslateZPx}
              onChange={(e) => handleDisplay3DChange("capTranslateZPx", clampNum(e.target.value, 0, 10, 3))}
              inputProps={{ min: 0, max: 10, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Seam height (px) (${DEFAULT_DISPLAY_3D.seamHeightPx})`}
              value={display3D.seamHeightPx ?? DEFAULT_DISPLAY_3D.seamHeightPx}
              onChange={(e) => handleDisplay3DChange("seamHeightPx", clampNum(e.target.value, 1, 8, 3))}
              inputProps={{ min: 1, max: 8, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Hinge hold (ms) (${DEFAULT_DISPLAY_3D.hingeHoldMs})`}
              value={display3D.hingeHoldMs ?? DEFAULT_DISPLAY_3D.hingeHoldMs}
              onChange={(e) => handleDisplay3DChange("hingeHoldMs", clampNum(e.target.value, 0, 50, 10))}
              inputProps={{ min: 0, max: 50, step: 2 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Char jitter (ms) (${DEFAULT_DISPLAY_3D.charJitterMs})`}
              value={display3D.charJitterMs ?? DEFAULT_DISPLAY_3D.charJitterMs}
              onChange={(e) => handleDisplay3DChange("charJitterMs", clampNum(e.target.value, 0, 20, 2))}
              inputProps={{ min: 0, max: 20, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Bottom overshoot (deg) (${DEFAULT_DISPLAY_3D.bottomOvershootDeg})`}
              value={display3D.bottomOvershootDeg ?? DEFAULT_DISPLAY_3D.bottomOvershootDeg}
              onChange={(e) => handleDisplay3DChange("bottomOvershootDeg", clampNum(e.target.value, 0, 8, 0))}
              inputProps={{ min: 0, max: 8, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Cap width (px) (${DEFAULT_DISPLAY_3D.capWidthPx})`}
              value={display3D.capWidthPx ?? DEFAULT_DISPLAY_3D.capWidthPx}
              onChange={(e) => handleDisplay3DChange("capWidthPx", clampNum(e.target.value, 3, 16, 7))}
              inputProps={{ min: 3, max: 16, step: 1 }}
              size="small"
            />
            <TextField
              type="number"
              label={`Cap height (px) (${DEFAULT_DISPLAY_3D.capHeightPx})`}
              value={display3D.capHeightPx ?? DEFAULT_DISPLAY_3D.capHeightPx}
              onChange={(e) => handleDisplay3DChange("capHeightPx", clampNum(e.target.value, 4, 24, 12))}
              inputProps={{ min: 4, max: 24, step: 1 }}
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={display3D.showThicknessEdge}
                  onChange={(e) => handleDisplay3DChange("showThicknessEdge", e.target.checked)}
                />
              }
              label="Show thickness edge"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={display3D.phaseLighting}
                  onChange={(e) => handleDisplay3DChange("phaseLighting", e.target.checked)}
                />
              }
              label="Phase-based lighting"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={display3D.edgeDarkening !== false}
                  onChange={(e) => handleDisplay3DChange("edgeDarkening", e.target.checked)}
                />
              }
              label="Edge darkening near 90°"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetParams}>Reset to defaults</Button>
          <Button onClick={() => setParamsModalOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <div className="title-logos-grid">
        <div key={`${brand.id}-${animationKey}`} className="title-logos-card">
          <div
            className="title-logos-frame"
            style={{ borderColor: FRAME_COLOR, backgroundColor: FRAME_COLOR }}
          >
            <div className="title-logos-display">
              <div className="title-logos-row-wrap">
                <SplitFlapRow
                  textSegments={[brand.text]}
                  speeds={[TITLE_LOGO_SETTINGS.speedMs]}
                  rowIndex={0}
                  isClock={true}
                  compact={false}
                  segmentBgColor="transparent"
                  segmentUpperFlapColor={brand.upperFlap}
                  segmentLowerFlapColor={brand.lowerFlap}
                  characterColor={CHARACTER_COLOR}
                  fontWeight={TITLE_LOGO_SETTINGS.bold ? "bold" : "normal"}
                  charStaggerMs={TITLE_LOGO_SETTINGS.charStaggerMs}
                  flipDurationMs={flipDurationMs}
                  use3dFlip={use3d}
                  onFlapComplete={onFlapComplete}
                  boardBgColor={BOARD_BG}
                  display3DConfig={display3D}
                  className="title-logos-splitflap-row"
                  tileWidth="clamp(40px, 5vw, 72px)"
                  tileHeight="clamp(52px, 6.5vw, 88px)"
                />
                <div className="title-logos-msi3-sup-wrap" aria-hidden="true">
                  <SplitFlapRow
                    textSegments={[brand.superscript]}
                    speeds={[TITLE_LOGO_SETTINGS.speedMs]}
                    rowIndex={0}
                    isClock={true}
                    compact={false}
                    segmentBgColor="transparent"
                    segmentUpperFlapColor={brand.upperFlap}
                    segmentLowerFlapColor={brand.lowerFlap}
                    characterColor={CHARACTER_COLOR}
                    fontWeight={TITLE_LOGO_SETTINGS.bold ? "bold" : "normal"}
                    charStaggerMs={TITLE_LOGO_SETTINGS.charStaggerMs}
                    flipDurationMs={flipDurationMs}
                    use3dFlip={use3d}
                    onFlapComplete={onFlapComplete}
                    boardBgColor={BOARD_BG}
                    display3DConfig={display3D}
                    lowerFlapChar=" "
                    className="title-logos-splitflap-row"
                    tileWidth="clamp(40px, 5vw, 72px)"
                    tileHeight="clamp(52px, 6.5vw, 88px)"
                  />
                </div>
              </div>
            </div>
          </div>
          <p className="title-logos-label">{brand.label}</p>
        </div>
      </div>
    </div>
  );
};

export default MSI3OnlyPage;
